import React, { useEffect, useState, useRef } from "react";
import videojs from "video.js";
import { IVideoProps } from "./interface";
import "./index.less";
import ZMessage from "@molecules/ZMessage";
import ZButton from "@atoms/ZButton";
import { validateYoutubeLink, getYoutubeID } from "@/Utils";
import { DefaultErrorMessage } from "@/Contracts";
import ZIcon from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import ZVideoPlayButton from "@v2assets/PlayButton.svg";

const ZVideo: React.FC<IVideoProps> = ({
	video,
	className,
	id,
	isPreview = false,
	showPlayButton = false,
	onClick,
	onPlay,
	loop = false,
	autoPlay = false,
	muted = false,
	hasLoading = false,
	showPlayBtnOnPause = false,
	thumbnail,
	handleVideoTimeUpdate
}) => {
	const videoBlockRef = useRef<HTMLVideoElement>(null);

	const [isVideoError, setIsVideoError] = useState(false);
	const [youtubeId, setYoutubeId] = useState<string | null>(null);

	const [loading, setLoading] = useState(hasLoading);

	const [customButtonVisible, setCustomButtonVisible] = useState(
		showPlayButton && !autoPlay
	);

	const videoPlayerControls = {
		controls: !isPreview,
		controlBar: {
			fullscreenToggle: true,
			volumePanel: {
				inline: false,
				volumeControl: { vertical: true }
			}
		},
		autoplay: autoPlay,
		preload: "metadata",
		loop,
		muted,
		liveui: true,
		notSupportedMessage: DefaultErrorMessage,
		playbackRates: [0.25, 0.5, 0.75, 1, 1.35, 1.5, 1.75, 2]
	};

	useEffect(() => {
		const isYoutube = validateYoutubeLink(video);
		if (isYoutube) {
			const youtubeVideoID = getYoutubeID(video);
			setYoutubeId(youtubeVideoID);
		}
	}, []);

	useEffect(() => {
		setTimeout(() => {
			try {
				if (videoBlockRef.current && !!video.length) {
					if (videojs.getPlayer(`video-player`)) {
						videojs(`video-player`).dispose();
					}
					videojs(`video-player`, videoPlayerControls);
				}
			} catch (error) {
				console.error(error);
			}
		}, 0);
	}, [video]);

	const onVideoPlay = (url: string) => {
		const isFirefox = "InstallTrigger" in window;
		const isMKV = url.endsWith(".mkv");
		if (videoBlockRef.current && isMKV) {
			const canPlay = videoBlockRef.current.canPlayType("video/mkv");

			// canPlay(empty string) : Video format is not supported and definitely cannot be played.
			if (canPlay === "" && isFirefox && !isVideoError) {
				ZMessage.error(
					"MKV/x-matroska format is not supported by Firefox browser. Please open the document in Chrome browser to view the video"
				);
				setIsVideoError(true);
				return;
			}
		}
		onPlay?.();
	};

	const handlePlay = (shouldPlay = false) => {
		if (shouldPlay) {
			videoBlockRef.current?.play();
		}
		setCustomButtonVisible(false);
		!isPreview && onVideoPlay(video);
	};

	const handleVideoStopOrPause = () => {
		showPlayButton && showPlayBtnOnPause && setCustomButtonVisible(true);
	};

	const handleCanPlay = () => {
		setLoading(false);
	};

	return (
		<div className="z-video-container">
			{!!youtubeId?.length ? (
				<div className="youtube-block">
					<iframe
						title="Youtube Video"
						// className="w-full"
						width={isPreview ? "100%" : 720}
						height={isPreview ? "auto" : 480}
						src={`https://www.youtube.com/embed/${youtubeId}${
							isPreview ? "?controls=0&showinfo=0&autohide=1" : ""
						}`}
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					></iframe>
				</div>
			) : (
				<>
					<video
						id={id}
						ref={videoBlockRef}
						height="100%"
						onClick={onClick}
						onPlay={() => handlePlay(false)}
						onCanPlay={handleCanPlay}
						onPause={handleVideoStopOrPause}
						onEnded={handleVideoStopOrPause}
						controls={!isPreview}
						preload="auto"
						autoPlay={autoPlay}
						poster={thumbnail}
						loop={loop}
						muted={muted}
						onTimeUpdate={() =>
							handleVideoTimeUpdate?.(videoBlockRef.current)
						}
						className={`video-js z-video ${
							isPreview ? "preview-mode pointer-events-none" : ""
						} ${
							showPlayButton ? "hide-big-button" : ""
						} ${className}`}
					>
						{/* HEVC and H.265 codec videos will only work in IE, Edge and Safari - Chrome and Firefox don't it support yet */}
						{/* HEVC video won't work in Chrome/Firefox - only audio will play - there's no way to render any fallback/error message either */}
						{/* Do NOT change the order of these source tags - the order matters - more specific codecs are rendered first */}
						<source src={video} type="video/mp4; codecs=hevc" />
						<source src={video} type="video/mp4; codecs=hvc1" />
						<source src={video} type="video/mp4; codecs=avc1" />
						<source src={video} type="video/mp4" />
						<source src={video} type="video/mkv" />
						<source src={video} type="video/webm; codecs=vp9" />
						<source src={video} type="video/webm" />
						Your browser does not support HTML5 video.
					</video>
					{!loading &&
						customButtonVisible &&
						(showPlayBtnOnPause ? (
							<div
								className="z-video-custom-play-button"
								onClick={() => handlePlay(true)}
							>
								{<ZVideoPlayButton />}
							</div>
						) : (
							<ZButton
								type="primary"
								className="z-video-play-button px-6"
								onClick={() => handlePlay(true)}
							>
								Play
							</ZButton>
						))}
					{loading && (
						<div className="z-video-loading">
							<ZIcon
								name={EIcons.VIDEOLOADING}
								color={EColors.GRAY}
								colorVariant={4}
								size="xl"
								className="animate-spin"
							/>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default ZVideo;
